import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthWrapper';
import { useRegenerateToken } from '@/hooks/api/useAuth';
import { HistoryProvider } from '@/contexts/HistoryContext';
import { useTabPersistence, ProfileTab } from '@/hooks/useTabPersistence';
import Footer from '@/components/Footer';
import Card from '@/components/ui/Card';
import ProfileSection from '@/components/profile/ProfileSection';
import WalletPage from '@/components/wallet/WalletPage';
import ProfileTransactions from '@/components/profile/ProfileTransactions';
import ChangeUsernameModal from '@/components/profile/ChangeUsernameModal';
import ChangeEmailModal from '@/components/profile/ChangeEmailModal';
import ChangePasswordModal from '@/components/profile/ChangePasswordModal';
import BridgePanel from '@/components/bridge/BridgePanel';

const MOBILE_BREAKPOINT = 768;

interface ProfilePageProps {
  isSidebarVisible?: boolean;
}

const ProfilePage: React.FC<ProfilePageProps> = ({
  isSidebarVisible = true,
}) => {
  const { user, changePassword, refreshUser } = useAuth();
  const regenerateTokenMutation = useRegenerateToken();
  const { activeTab, handleTabChange } = useTabPersistence();
  const username = user?.name || 'Username';
  const email = user?.email || 'user@example.com';
  const [isChangeUsernameModalOpen, setIsChangeUsernameModalOpen] =
    useState(false);
  const [usernameChangeLoading, setUsernameChangeLoading] = useState(false);
  const [usernameChangeError, setUsernameChangeError] = useState('');
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState('');
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [isGenerateTokenModalOpen, setIsGenerateTokenModalOpen] =
    useState(false);
  const [tokenActionError, setTokenActionError] = useState('');
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  // URL/tab synchronization is fully handled inside useTabPersistence

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    padding: '24px',
    gap: '24px',
  };

  const contentAreaStyles: React.CSSProperties = {
    flex: 1,
    maxWidth: '800px',
    margin: isSidebarVisible ? '0 auto' : '0 auto',
    width: '100%',
  };

  const tabNavigationStyles: React.CSSProperties = {
    display: 'flex',
    borderBottom: '1px solid var(--outline-base_em, #F4F4F6)',
    marginBottom: '32px',
    gap: '32px',
  };

  const tabStyles: React.CSSProperties = {
    padding: '12px 0',
    fontSize: '16px',
    fontWeight: 500,
    color: 'var(--text-med_em, #5A5D6B)',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    position: 'relative',
    transition: 'color 0.15s ease-in-out',
    outline: 'none',
  };

  const activeTabStyles: React.CSSProperties = {
    ...tabStyles,
    color: 'var(--text-high_em, #0A0C11)',
    fontWeight: 600,
  };

  const tabUnderlineStyles: React.CSSProperties = {
    position: 'absolute',
    bottom: '-1px',
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: 'var(--text-high_em)', // Black underline for active tab
    borderRadius: '1px',
  };

  const sectionStyles: React.CSSProperties = {
    marginBottom: '32px',
  };

  // Main section titles (Overview, Settings) - Heading 3/Medium from Figma
  const mainSectionTitleStyles: React.CSSProperties = {
    color: 'var(--text-high_em, #0A0C11)',
    fontFamily: 'var(--font-family-primary, Inter)',
    fontSize: 'var(--font-size-heading_3, 32px)',
    fontStyle: 'normal',
    fontWeight: 'var(--font-weight-default-medium, 600)',
    lineHeight: 'var(--font-line_height-heading_3, 40px)',
    letterSpacing: 'var(--font-letter_spacing-none, 0px)',
    margin: 0,
    marginBottom: '16px',
  };

  // Subsection titles (Account details, Safety, Token) - Heading 1/Medium from Figma
  const subSectionTitleStyles: React.CSSProperties = {
    color: 'var(--text-high_em, #0A0C11)',
    fontFamily: 'var(--font-family-primary, Inter)',
    fontSize: 'var(--font-size-heading_1, 22px)',
    fontStyle: 'normal',
    fontWeight: 'var(--font-weight-default-medium, 600)',
    lineHeight: 'var(--font-line_height-heading_1, 32px)',
    letterSpacing: 'var(--font-letter_spacing-none, 0px)',
    margin: 0,
    marginBottom: '16px',
  };

  const placeholderStyles: React.CSSProperties = {
    color: 'var(--text-med_em, #5A5D6B)',
    fontSize: '14px',
    fontStyle: 'italic',
  };

  const handleTabClick = (tab: ProfileTab) => {
    handleTabChange(tab);
  };

  const handleChangeUsername = () => {
    setIsChangeUsernameModalOpen(true);
  };

  const handleChangeEmail = () => {
    setIsChangeEmailModalOpen(true);
  };

  const handleCloseEmailModal = () => {
    setIsChangeEmailModalOpen(false);
    setEmailChangeError('');
  };

  const handleEmailSubmit = async (newEmail: string) => {
    setEmailChangeLoading(true);
    setEmailChangeError('');

    try {
      // TODO: Implement email change API call
      console.log('Changing email to:', newEmail);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // For now, just close the modal
      setIsChangeEmailModalOpen(false);

      // TODO: Refresh user data or update context
      // await refreshUser();
    } catch (error) {
      setEmailChangeError(
        error instanceof Error ? error.message : 'Failed to change email'
      );
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleChangePassword = () => {
    setIsChangePasswordModalOpen(true);
  };

  const handleClosePasswordModal = () => {
    setIsChangePasswordModalOpen(false);
    setPasswordChangeError('');
  };

  const handlePasswordSubmit = async (
    currentPassword: string,
    newPassword: string
  ) => {
    setPasswordChangeLoading(true);
    setPasswordChangeError('');

    try {
      await changePassword(currentPassword, newPassword, newPassword);

      // Close modal on success
      setIsChangePasswordModalOpen(false);

      // TODO: Show success message
      console.log('Password changed successfully');
    } catch (error) {
      setPasswordChangeError(
        error instanceof Error ? error.message : 'Failed to change password'
      );
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const handleCloseUsernameModal = () => {
    setIsChangeUsernameModalOpen(false);
    setUsernameChangeError('');
  };

  const handleUsernameSubmit = async (newUsername: string) => {
    setUsernameChangeLoading(true);
    setUsernameChangeError('');

    try {
      // Not implemented in OpenAPI; backend endpoint missing
      throw new Error('Changing username is not available');
    } catch (error) {
      setUsernameChangeError(
        error instanceof Error ? error.message : 'Failed to change username'
      );
    } finally {
      setUsernameChangeLoading(false);
    }
  };

  const handleGenerateToken = async () => {
    setTokenActionError('');
    try {
      await regenerateTokenMutation.mutateAsync();
      await refreshUser();
    } catch (error) {
      setTokenActionError(
        error instanceof Error ? error.message : 'Failed to regenerate token'
      );
    }
  };

  return (
    <div style={containerStyles}>
      <div style={contentAreaStyles}>
        {/* Unified Card Container */}
        <Card variant="default" padding="lg">
          {/* Settings Section */}
          <div style={sectionStyles}>
            <h2 style={mainSectionTitleStyles}>Settings</h2>

            {/* Tab Navigation — on mobile only Profile tab is shown */}
            <div style={tabNavigationStyles}>
              <button
                style={activeTab === 'profile' ? activeTabStyles : tabStyles}
                onClick={() => handleTabClick('profile')}
              >
                Profile
                {activeTab === 'profile' && <div style={tabUnderlineStyles} />}
              </button>
              {!isMobile && (
                <>
                  <button
                    style={activeTab === 'wallet' ? activeTabStyles : tabStyles}
                    onClick={() => handleTabClick('wallet')}
                  >
                    Wallet
                    {activeTab === 'wallet' && <div style={tabUnderlineStyles} />}
                  </button>
                  <button
                    style={activeTab === 'bridge' ? activeTabStyles : tabStyles}
                    onClick={() => handleTabClick('bridge')}
                  >
                    Bridge
                    {activeTab === 'bridge' && <div style={tabUnderlineStyles} />}
                  </button>
                  <button
                    style={activeTab === 'history' ? activeTabStyles : tabStyles}
                    onClick={() => handleTabClick('history')}
                  >
                    History
                    {activeTab === 'history' && <div style={tabUnderlineStyles} />}
                  </button>
                </>
              )}
            </div>

            {/* Tab Content — on mobile only Profile section is shown */}
            <div
              style={{
                transition: 'opacity 0.2s ease-in-out',
                opacity: 1,
                minHeight: '200px',
              }}
            >
              {(isMobile || activeTab === 'profile') && (
                <ProfileSection
                  username={username}
                  email={email}
                  token={user?.token || 'No token available'}
                  onChangeUsername={handleChangeUsername}
                  onChangeEmail={handleChangeEmail}
                  onChangePassword={handleChangePassword}
                  onGenerateToken={handleGenerateToken}
                />
              )}

              {!isMobile && activeTab === 'wallet' && (
                <div style={sectionStyles}>
                  <h3 style={subSectionTitleStyles}>Wallet</h3>
                  <WalletPage />
                </div>
              )}

              {!isMobile && activeTab === 'bridge' && (
                <div style={sectionStyles}>
                  <h3 style={subSectionTitleStyles}>Bridge</h3>
                  <BridgePanel />
                </div>
              )}

              {!isMobile && activeTab === 'history' && (
                <div style={sectionStyles}>
                  <h3 style={subSectionTitleStyles}>History</h3>
                  <ProfileTransactions />
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      <Footer />

      {/* Change Username Modal */}
      <ChangeUsernameModal
        isOpen={isChangeUsernameModalOpen}
        onClose={handleCloseUsernameModal}
        currentUsername={username}
        onSubmit={handleUsernameSubmit}
        isLoading={usernameChangeLoading}
        error={usernameChangeError}
      />

      {/* Change Email Modal */}
      <ChangeEmailModal
        isOpen={isChangeEmailModalOpen}
        onClose={handleCloseEmailModal}
        currentEmail={email}
        onSubmit={handleEmailSubmit}
        isLoading={emailChangeLoading}
        error={emailChangeError}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={handleClosePasswordModal}
        onSubmit={handlePasswordSubmit}
        isLoading={passwordChangeLoading}
        error={passwordChangeError}
      />
    </div>
  );
};

export default ProfilePage;
